import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import Select2Dropdown from '../common/Select2Dropdown';
import { openFilePreview } from '../common/FilePreviewModal';
import PopoutWindow from '../common/PopoutWindow';
import ShippingLinePayablesForm from './ShippingLinePayablesForm';
import OtherPayablesForm from './OtherPayablesForm';

const API_PAYABLES = '/api/admin/shipping-line-payables/';
const API_OTHER_PAYABLES = '/api/admin/other-payables/';

const getAuthHeaders = (isForm = false) => {
    const headers = { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` };
    if (!isForm) headers['Content-Type'] = 'application/json';
    return headers;
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
};

const DOCUMENT_DISPLAY_ORDER = {
    'BL': 0,
    'PROFORMA INVOICE': 1,
    'PROOF OF PAYMENT': 2,
    'TAX INVOICE': 3,
    'RECEIPT': 4,
};

const getTransferCharge = (amount, brackets) => {
    const numericAmount = parseFloat(amount);
    if (!numericAmount || Number.isNaN(numericAmount) || numericAmount <= 0) return 0;
    const normalizedBrackets = Array.isArray(brackets) ? brackets : [];
    const matchedBracket = normalizedBrackets.find(bracket => {
        const minAmount = parseFloat(bracket.min_amount ?? bracket.min ?? bracket.minimum_amount ?? 0);
        const maxValue = bracket.max_amount ?? bracket.max ?? bracket.maximum_amount;
        const maxAmount = maxValue === null || maxValue === '' || maxValue === undefined ? Infinity : parseFloat(maxValue);
        return numericAmount >= minAmount && numericAmount <= maxAmount;
    });
    return matchedBracket ? parseFloat(matchedBracket.rate ?? matchedBracket.amount ?? matchedBracket.charge ?? 0) : 0;
};

const normalizeDocumentType = (docType) => {
    const value = (docType || '').trim().toUpperCase();
    if (['BL', 'BILL OF LADING', 'OBL', 'HBL', 'MBL'].includes(value)) return 'BL';
    if (value.includes('PROFORMA')) return 'PROFORMA INVOICE';
    if (value.includes('PROOF OF PAYMENT')) return 'PROOF OF PAYMENT';
    if (value.includes('TAX INVOICE')) return 'TAX INVOICE';
    if (value === 'RECEIPT' || value.includes('RECEIPT')) return 'RECEIPT';
    return value;
};

const getUniqueDocumentsByType = (docs) => {
    const uniqueByType = new Map();
    (docs || []).forEach((doc) => {
        const typeKey = normalizeDocumentType(doc?.document_type) || 'OTHER';
        if (!uniqueByType.has(typeKey)) {
            uniqueByType.set(typeKey, doc);
        }
    });
    return Array.from(uniqueByType.values());
};

const BulkPayForm = ({
    payables,
    onClose,
    onSuccess,
    toastContainerId,
    popoutMode,
    mode = 'payment', // 'payment' for Stage 2, 'approval' for Stage 3
}) => {
    const toastOptions = toastContainerId ? { containerId: toastContainerId } : undefined;
    const notifyError = (msg) => toast.error(msg, toastOptions);
    const notifySuccess = (msg) => toast.success(msg, toastOptions);

    const [bankOptions, setBankOptions] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});
    const [stage2Data, setStage2Data] = useState({
        bank: '',
        cheque_no: '',
        collected_by: '',
        paid_by: ''
    });
    const [stage3Data, setStage3Data] = useState({
        payment_date: new Date().toISOString().split('T')[0],
        tiss_tips: '',
        payment_source: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [showRejectInput, setShowRejectInput] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectionTarget, setRejectionTarget] = useState(null);
    const [showConfirmApprove, setShowConfirmApprove] = useState(false);
    const [showConfirmReject, setShowConfirmReject] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [viewingPayable, setViewingPayable] = useState(null);

    const vendorName = payables.length > 0 ? (payables[0].shipping_line_name || payables[0].vendor_name || '-') : '-';

    // Group payables by payment details for Stage 3 approval
    const paymentDetailsGroups = useMemo(() => {
        if (mode !== 'approval') return [];
        const groups = {};
        payables.forEach(p => {
            const key = `${p.bank_account || ''}|${p.cheque_no || ''}|${p.collected_by || ''}|${p.paid_by || ''}`;
            if (!groups[key]) {
                groups[key] = {
                    bank_account: p.bank_account || '',
                    cheque_no: p.cheque_no || '',
                    collected_by: p.collected_by || '',
                    paid_by: p.paid_by || '',
                    payables: []
                };
            }
            groups[key].payables.push(p);
        });
        return Object.values(groups);
    }, [payables, mode]);

    // Detect whether Stage 3 approval needs missing Stage 2 payment details entered
    const stage2DetailsMissing = useMemo(() => {
        if (mode !== 'approval') return false;
        return payables.some(p => {
            const bank = p.bank_account || '';
            const isCashMethod = bank === 'CASH';
            return !bank || !p.collected_by || !p.paid_by || (!isCashMethod && !p.cheque_no);
        });
    }, [payables, mode]);

    // Pre-fill the missing Stage 2 payment details form from the first payable that has values
    useEffect(() => {
        if (mode !== 'approval' || !stage2DetailsMissing) return;
        const first = payables.find(p => {
            const bank = p.bank_account || '';
            const isCashMethod = bank === 'CASH';
            return bank && p.collected_by && p.paid_by && (isCashMethod || p.cheque_no);
        });
        setStage2Data(prev => ({
            bank: first?.bank_account || prev.bank || '',
            cheque_no: (first?.bank_account === 'CASH' ? '' : (first?.cheque_no || '')) || prev.cheque_no || '',
            collected_by: first?.collected_by || prev.collected_by || '',
            paid_by: first?.paid_by || prev.paid_by || ''
        }));
    }, [mode, stage2DetailsMissing, payables]);

    // Fetch bank options from the shared get_options endpoint
    useEffect(() => {
        const fetchBanks = async () => {
            try {
                const res = await fetch(`${API_PAYABLES}get_options/`, { headers: getAuthHeaders() });
                if (res.ok) {
                    const data = await res.json();
                    if (data.banks) setBankOptions(data.banks);
                }
            } catch (e) {
                console.error('Failed to fetch bank options', e);
            }
        };
        fetchBanks();
    }, []);

    // Running totals from selected payables
    const totals = useMemo(() => {
        const result = {};
        payables.forEach(p => {
            const curr = p.currency || 'TZS';
            if (!result[curr]) result[curr] = { net: 0, vat: 0, gross: 0 };

            // Apply invoiceCurrencyDifferent logic for totals calculation
            const pInvoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;
            const pHasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);
            const multiplier = (pInvoiceCurrencyDifferent && pHasOldItems && p.usd_rate) ? parseFloat(p.usd_rate) : 1;

            const net = parseFloat(p.net_amount || 0) * multiplier;
            const vat = parseFloat(p.total_vat || 0) * multiplier;
            result[curr].net += net;
            result[curr].vat += vat;
            result[curr].gross += net + vat;
        });
        // Round to 2 decimal places
        Object.keys(result).forEach(curr => {
            result[curr].net = Math.round(result[curr].net * 100) / 100;
            result[curr].vat = Math.round(result[curr].vat * 100) / 100;
            result[curr].gross = Math.round(result[curr].gross * 100) / 100;
        });
        return result;
    }, [payables]);

    // TISS/TIPS amount calculation for Stage 3 — bracket-based per individual payable
    const isTissOrTipsSelected = stage3Data.tiss_tips === 'TISS' || stage3Data.tiss_tips === 'TIPS';

    // Helper: gross TZS for a given payable
    const getPayableGrossTzs = (p) => {
        const pInvoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;
        const pHasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);
        const multiplier = (pInvoiceCurrencyDifferent && pHasOldItems && p.usd_rate) ? parseFloat(p.usd_rate) : 1;
        const net = parseFloat(p.net_amount || 0) * multiplier;
        const vat = parseFloat(p.total_vat || 0) * multiplier;
        const usdRate = parseFloat(p.usd_rate || 1);
        const grossInCurrency = Math.round((net + vat) * 100) / 100;
        return p.currency === 'TZS' ? grossInCurrency : grossInCurrency * usdRate;
    };

    // Per-payable gross (TZS) and TISS/TIPS amount using that payable's bank account brackets
    const perPayableTiss = useMemo(() => {
        if (!isTissOrTipsSelected) return {};
        const result = {};
        payables.forEach(p => {
            const grossTzs = getPayableGrossTzs(p);
            // Use the payable's saved bank or the Stage 2 bank being entered now
            const bankId = p.bank_account || stage2Data.bank || '';
            const bankInfo = bankOptions.find(b => String(b.id) === String(bankId)) || null;
            const brackets = bankInfo
                ? (stage3Data.tiss_tips === 'TIPS' ? (bankInfo.tips_brackets || []) : (bankInfo.tiss_brackets || []))
                : [];
            result[p.id] = Math.round(getTransferCharge(grossTzs, brackets) * 100) / 100;
        });
        return result;
    }, [payables, isTissOrTipsSelected, stage3Data.tiss_tips, bankOptions, stage2Data.bank]);

    const stage3TissTipsAmount = useMemo(() => {
        return Object.values(perPayableTiss).reduce((sum, v) => sum + v, 0);
    }, [perPayableTiss]);

    const perPayableGepg = useMemo(() => {
        const result = {};
        payables.forEach(p => {
            if (p.control_no_type !== 'GEPG') {
                result[p.id] = 0;
                return;
            }
            const bankId = p.bank_account || stage2Data.bank || '';
            const bankInfo = bankOptions.find(b => String(b.id) === String(bankId)) || null;
            const brackets = bankInfo ? (bankInfo.gepg_brackets || []) : [];
            result[p.id] = Math.round(getTransferCharge(getPayableGrossTzs(p), brackets) * 100) / 100;
        });
        return result;
    }, [payables, bankOptions, stage2Data.bank]);

    const stage3GepgAmount = useMemo(() => {
        return Object.values(perPayableGepg).reduce((sum, v) => sum + v, 0);
    }, [perPayableGepg]);

    const stage3NewPayableAmount = useMemo(() => {
        if (!isTissOrTipsSelected) return 0;
        const totalGross = Object.values(totals).reduce((sum, curr) => sum + curr.gross, 0);
        return Math.round((totalGross + stage3TissTipsAmount + stage3GepgAmount) * 100) / 100;
    }, [totals, stage3TissTipsAmount, stage3GepgAmount, isTissOrTipsSelected]);

    // Helper function to calculate display amount with invoiceCurrencyDifferent logic
    const calculateDisplayAmount = (payable) => {
        const pInvoiceCurrencyDifferent = payable.currency === 'TZS' && parseFloat(payable.usd_rate || 0) > 100;
        const pHasOldItems = payable.items && payable.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && payable.items.some(item => !item.total_amount);
        const pRawTotal = parseFloat(payable.total_amount || 0);
        const pDisplayAmount = pInvoiceCurrencyDifferent && pHasOldItems && payable.usd_rate
            ? Math.round(pRawTotal * parseFloat(payable.usd_rate) * 100) / 100
            : pRawTotal;
        return pDisplayAmount;
    };

    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const getRejectionOptions = () => {
        if (mode === 'payment') {
            // Stage 2 approval: current status is APPROVED 1
            return [
                { id: 'CREATOR', text: 'CREATOR' },
                { id: 'STAGE 1', text: 'STAGE 1' },
            ];
        }
        // Stage 3 approval: current status is APPROVED 2
        return [
            { id: 'CREATOR', text: 'CREATOR' },
            { id: 'STAGE 1', text: 'STAGE 1' },
            { id: 'STAGE 2', text: 'STAGE 2' },
        ];
    };

    const validatePayment = () => {
        if (mode === 'approval') {
            if (!stage3Data.payment_date) { notifyError('Please enter Payment Date'); return false; }
            if (!stage3Data.tiss_tips) { notifyError('Please select TISS/TIPS'); return false; }
            if (stage2DetailsMissing) {
                if (!stage2Data.bank) { notifyError('Please select a Payment Method (Bank/Cash)'); return false; }
                if (stage2Data.bank !== 'CASH' && !stage2Data.cheque_no.trim()) { notifyError('Please enter Cheque/Ref No'); return false; }
                if (!stage2Data.collected_by.trim()) { notifyError('Please enter Collected By / Paid To'); return false; }
                if (!stage2Data.paid_by) { notifyError('Please select Paid By'); return false; }
            }
            return true;
        }
        if (!stage2Data.bank) { notifyError('Please select a Payment Method (Bank/Cash)'); return false; }
        if (stage2Data.bank !== 'CASH' && !stage2Data.cheque_no.trim()) { notifyError('Please enter Cheque/Ref No'); return false; }
        if (!stage2Data.collected_by.trim()) { notifyError('Please enter Collected By / Paid To'); return false; }
        if (!stage2Data.paid_by) { notifyError('Please select Paid By'); return false; }
        return true;
    };

    const approveAll = async () => {
        setShowConfirmApprove(false);
        if (!validatePayment()) return;

        setSubmitting(true);
        setProgress({ done: 0, total: payables.length });

        const newStatus = mode === 'approval' ? 'APPROVED 3' : (stage2Data.bank === 'CASH' ? 'APPROVED 3' : 'APPROVED 2');
        // Link multiple Stage 2 payables together so they appear as a group in Stage 3
        const bulkGroupId = (mode === 'payment' && payables.length > 1)
            ? (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`)
            : null;
        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < payables.length; i++) {
            const p = payables[i];
            try {
                const payload = new FormData();
                const apiUrl = p._payable_source === 'shipping' ? API_PAYABLES : API_OTHER_PAYABLES;

                if (mode === 'payment') {
                    // Stage 2 payment mode - include payment details and link bulk group
                    payload.append('bank_account', stage2Data.bank);
                    payload.append('cheque_no', stage2Data.bank === 'CASH' ? '' : (stage2Data.cheque_no || ''));
                    payload.append('collected_by', stage2Data.collected_by);
                    payload.append('paid_by', stage2Data.paid_by);
                    if (bulkGroupId) payload.append('bulk_group_id', bulkGroupId);
                    if (stage2Data.bank === 'CASH') {
                        payload.append('payment_date', new Date().toISOString().split('T')[0]);
                        payload.append('tiss_tips', 'NO TISS/TIPS');
                    }
                } else {
                    // Stage 3 approval mode - include Stage 3 details with per-payable TISS/TIPS amount
                    const resolvedBank = p.bank_account || stage2Data.bank;
                    const resolvedCash = resolvedBank === 'CASH';
                    const resolvedCheque = resolvedCash ? '' : (p.cheque_no || stage2Data.cheque_no || '');
                    const resolvedCollectedBy = p.collected_by || stage2Data.collected_by || '';
                    const resolvedPaidBy = p.paid_by || stage2Data.paid_by || '';

                    payload.append('payment_date', stage3Data.payment_date);
                    payload.append('tiss_tips', stage3Data.tiss_tips);
                    if (isTissOrTipsSelected) {
                        payload.append('tiss_tips_amount', perPayableTiss[p.id] ?? 0);
                    } else {
                        payload.append('tiss_tips_amount', '0');
                    }
                    payload.append('gepg_amount', perPayableGepg[p.id] ?? 0);
                    payload.append('bank_account', resolvedBank);
                    payload.append('cheque_no', resolvedCheque);
                    payload.append('collected_by', resolvedCollectedBy);
                    payload.append('paid_by', resolvedPaidBy);
                }

                payload.append('status', newStatus);

                const res = await fetch(`${apiUrl}${p.id}/`, {
                    method: 'PATCH',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` },
                    body: payload
                });

                if (res.ok) successCount++;
                else failCount++;
            } catch (e) {
                failCount++;
            }
            setProgress({ done: i + 1, total: payables.length });
        }

        setSubmitting(false);

        if (failCount === 0) {
            notifySuccess(`All ${successCount} payable(s) ${mode === 'approval' ? 'approved' : 'processed'} successfully.`);
            onSuccess();
        } else {
            notifyError(`${successCount} ${mode === 'approval' ? 'approved' : 'processed'}, ${failCount} failed. Please check and retry.`);
            if (successCount > 0) onSuccess();
        }
    };

    const rejectAll = async () => {
        setShowConfirmReject(false);
        if (!rejectReason.trim()) { notifyError('Please enter a rejection reason'); return; }
        if (!rejectionTarget) { notifyError('Please select the stage to send to'); return; }

        setSubmitting(true);
        setProgress({ done: 0, total: payables.length });

        let successCount = 0;
        let failCount = 0;

        let targetStatus = 'REJECTED'; // CREATOR default
        if (rejectionTarget === 'STAGE 1') targetStatus = 'PENDING';
        if (rejectionTarget === 'STAGE 2') targetStatus = 'APPROVED 1';

        for (let i = 0; i < payables.length; i++) {
            const p = payables[i];
            try {
                const apiUrl = p._payable_source === 'shipping' ? API_PAYABLES : API_OTHER_PAYABLES;
                const res = await fetch(`${apiUrl}${p.id}/`, {
                    method: 'PATCH',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ status: targetStatus, rejection_reason: rejectReason.toUpperCase() })
                });
                if (res.ok) successCount++;
                else failCount++;
            } catch (e) {
                failCount++;
            }
            setProgress({ done: i + 1, total: payables.length });
        }

        setSubmitting(false);

        const targetLabel = getRejectionOptions().find(o => o.id === rejectionTarget)?.text || rejectionTarget;
        if (failCount === 0) {
            notifySuccess(`All ${successCount} payable(s) sent to ${targetLabel}.`);
            onSuccess();
        } else {
            notifyError(`${successCount} sent to ${targetLabel}, ${failCount} failed.`);
            if (successCount > 0) onSuccess();
        }
    };

    return (
        <div
            className="dialog-container"
            style={{
                width: '95%',
                maxWidth: popoutMode ? 'none' : '1400px',
                height: popoutMode ? '95vh' : 'auto',
                maxHeight: popoutMode ? '95vh' : '85vh',
                display: 'flex',
                flexDirection: 'column',
                margin: popoutMode ? '2vh auto' : '0 auto',
                fontFamily: 'Arial, sans-serif'
            }}
        >
            {/* Header */}
            <div className="dialog-header dis-flex-between">
                <div>
                    <h3>{mode === 'approval' ? 'BULK APPROVAL — STAGE 3' : 'BULK PAYMENT — STAGE 2 APPROVAL'}</h3>
                    <span style={{ fontSize: '0.9em', color: '#555' }}>
                        <strong style={{ color: '#fff' }}>VENDOR:{vendorName}</strong>
                        &nbsp;|&nbsp;
                        <strong>{payables.length}</strong> PAYABLE(S) SELECTED
                    </span>
                </div>
            </div>

            {/* Body */}
            <div className="dialog-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

                {/* Running Totals */}
                <div style={{ marginBottom: '20px', padding: '15px', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '4px' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>
                        <i className="fa fa-calculator"></i> Payment Totals
                    </h5>
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        {Object.entries(totals).map(([currency, data]) => (
                            <div key={currency} style={{ padding: '10px', background: 'white', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minWidth: '220px' }}>
                                <div style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '5px' }}>{currency}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Amount:</span> <strong>{formatCurrency(data.net)}</strong></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>VAT:</span> <strong>{formatCurrency(data.vat)}</strong></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#2e7d32', marginTop: '5px', paddingTop: '5px', borderTop: '1px dashed #ccc' }}>
                                    <span>TOTAL:</span> <strong>{formatCurrency(data.gross)}</strong>
                                </div>
                            </div>
                        ))}
                        {mode === 'approval' && paymentDetailsGroups.length > 0 && paymentDetailsGroups.map((group, idx) => (
                            <div key={idx} style={{ padding: '10px', background: 'white', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minWidth: '220px' }}>
                                <div style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '5px' }}>
                                    PAYMENT DETAILS {paymentDetailsGroups.length > 1 ? `(${group.payables.length} payable${group.payables.length > 1 ? 's' : ''})` : ''}
                                </div>
                                {paymentDetailsGroups.length > 1 && (
                                    <div style={{ fontSize: '0.8em', color: '#666', marginBottom: '5px' }}>
                                        {group.payables.map(p => p.reference_no || p.invoice_no || p.id).join(', ')}
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Method:</span> <strong>{bankOptions.find(b => b.id === group.bank_account)?.text || group.bank_account || '-'}</strong></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Cheque/Ref:</span> <strong>{group.cheque_no || '-'}</strong></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Collected By:</span> <strong>{(group.collected_by || '').toUpperCase() || '-'}</strong></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Paid By:</span> <strong>{[{ id: 'K & K', text: 'K AND K' }, { id: 'PAID BOC', text: 'PAID ON BEHALF OF CLIENT' }].find(o => o.id === group.paid_by)?.text || group.paid_by || '-'}</strong></div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Statement of Charges Table */}
                <div style={{ marginBottom: '20px' }}>
                    <h5 style={{ marginBottom: '10px', color: '#333' }}>
                        <i className="fa fa-list-alt"></i> Statement of Charges
                        <span style={{ fontSize: '0.8em', color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>
                            (Click a row to expand charge details and documents)
                        </span>
                    </h5>
                    <table className="table table-bordered" style={{ fontSize: '0.9em' }}>
                        <thead style={{ background: '#f8f9fa' }}>
                            <tr>
                                <th style={{ width: '30px' }}></th>
                                <th>Ref No</th>
                                <th>Job ID</th>
                                <th>IDF No</th>
                                <th>BL No</th>
                                <th>Customer</th>
                                <th>Currency</th>
                                <th>FX Rate</th>
                                <th style={{ textAlign: 'right' }}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payables.map(p => (
                                <React.Fragment key={p.id}>
                                    {/* Summary row — click to expand */}
                                    <tr
                                        style={{ cursor: 'pointer', background: expandedRows[p.id] ? '#fff9c4' : undefined }}
                                        onClick={() => toggleRow(p.id)}
                                    >
                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                            <i
                                                className={`fa fa-chevron-${expandedRows[p.id] ? 'down' : 'right'}`}
                                                style={{ color: '#555' }}
                                            ></i>
                                        </td>
                                        <td style={{ fontWeight: '600', color: '#6c757d', whiteSpace: 'nowrap' }}>
                                            <a
                                                href="#"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setViewingPayable(p); }}
                                                style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                                                title="View payable in new window"
                                            >
                                                {p.reference_no || '-'}
                                            </a>
                                        </td>
                                        <td>{p.job_number || '-'}</td>
                                        <td>{p.idf_number || '-'}</td>
                                        <td>{p.bl_number || '-'}</td>
                                        <td>{p.customer_name || '-'}</td>
                                        <td>{p.currency || '-'}</td>
                                        <td>{p.currency && p.currency !== 'TZS' ? (p.usd_rate || '-') : '-'}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#c0392b' }}>
                                            {formatCurrency(calculateDisplayAmount(p))}
                                        </td>
                                    </tr>

                                    {/* Expanded detail row */}
                                    {expandedRows[p.id] && (
                                        <tr>
                                            <td
                                                colSpan={9}
                                                style={{ padding: '0 20px 20px 45px', background: '#fffef5', borderTop: 'none' }}
                                            >
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '15px' }}>

                                                    {/* Charge Details */}
                                                    <div>
                                                        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                                                            <i className="fa fa-table" style={{ marginRight: '5px' }}></i>
                                                            Charge Details
                                                        </div>
                                                        <table className="table table-bordered table-sm" style={{ fontSize: '0.85em' }}>
                                                            <thead style={{ background: '#e8f5e9' }}>
                                                                <tr>
                                                                    <th style={{ textAlign: 'left' }}>CHARGE NAME</th>
                                                                    <th style={{ textAlign: 'left' }}>UNITS</th>
                                                                    <th style={{ textAlign: 'left' }}>RATE</th>
                                                                    {p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100 && <th style={{ textAlign: 'right' }}>{p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100 ? 'USD' : ''} AMOUNT</th>}
                                                                    <th style={{ textAlign: 'right' }}>AMOUNT</th>
                                                                    <th style={{ textAlign: 'right' }}>VAT</th>
                                                                    <th style={{ textAlign: 'right' }}>TOTAL</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(p.items || []).length > 0 ? (
                                                                    p.items.map((item, idx) => {
                                                                        const pInvoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;
                                                                        const isAdjustment = item.charge_name === 'ADJUSTMENT';
                                                                        const foreignAmount = parseFloat(item.foreign_amount || 0);
                                                                        const amount = parseFloat(item.amount || 0);
                                                                        const unitRate = parseFloat(item.unit_rate || 0);
                                                                        const units = parseFloat(item.units || 0);
                                                                        const vatAmount = parseFloat(item.vat_amount || 0);
                                                                        const totalAmount = parseFloat(item.total_amount || 0);
                                                                        const fxRate = parseFloat(p.usd_rate || 1);
                                                                        const fxRoundOff = p.fx_round_off !== false;

                                                                        // Calculate display amounts using OtherPayablesForm logic
                                                                        const displayForeignAmount = pInvoiceCurrencyDifferent && !isAdjustment
                                                                            ? (foreignAmount > 0 ? foreignAmount : units * unitRate)
                                                                            : 0;

                                                                        // For amount: if invoiceCurrencyDifferent and no foreign_amount saved, multiply by fx_rate
                                                                        const effectiveAmount = (pInvoiceCurrencyDifferent && !foreignAmount)
                                                                            ? amount * (fxRoundOff ? Math.round(fxRate) : fxRate)
                                                                            : amount;

                                                                        const displayAmount = isAdjustment ? (totalAmount || amount) : effectiveAmount;

                                                                        // For VAT: use saved vat_amount or calculate from effective_amount
                                                                        const displayVat = vatAmount > 0 ? vatAmount : (item.add_vat ? effectiveAmount * 0.18 : 0);

                                                                        // For Total: use saved total_amount or calculate from effective_amount
                                                                        const displayTotal = totalAmount > 0 ? totalAmount : (item.add_vat ? effectiveAmount * 1.18 : effectiveAmount);

                                                                        return (
                                                                        <tr key={idx}>
                                                                            <td style={{ textAlign: 'left' }}>{String(item.charge_name || '').toUpperCase()}</td>
                                                                            <td style={{ textAlign: 'left' }}>{`${item.units || ''} ${item.unit_type || ''}`.trim().toUpperCase()}</td>
                                                                            {isAdjustment ? (
                                                                                <td style={{ textAlign: 'left' }}></td>
                                                                            ) : (
                                                                                <td style={{ textAlign: 'left' }}>{formatCurrency(item.unit_rate)}</td>
                                                                            )}
                                                                            {pInvoiceCurrencyDifferent && (
                                                                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                                                                    {!isAdjustment && formatCurrency(displayForeignAmount)}
                                                                                </td>
                                                                            )}
                                                                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                                                                {formatCurrency(displayAmount)}
                                                                            </td>
                                                                            <td style={{ textAlign: 'right' }}>
                                                                                {formatCurrency(displayVat)}
                                                                            </td>
                                                                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                                                                {formatCurrency(displayTotal)}
                                                                            </td>
                                                                        </tr>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan={p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100 ? 7 : 6} className="text-center text-muted">No charge items found</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Documents */}
                                                    <div>
                                                        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                                                            <i className="fa fa-paperclip" style={{ marginRight: '5px' }}></i>
                                                            Documents
                                                        </div>
                                                        {(p.documents || []).length > 0 ? (
                                                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                                {getUniqueDocumentsByType(p.documents)
                                                                    .sort((left, right) => {
                                                                        const leftOrder = DOCUMENT_DISPLAY_ORDER[normalizeDocumentType(left.document_type)] ?? 99;
                                                                        const rightOrder = DOCUMENT_DISPLAY_ORDER[normalizeDocumentType(right.document_type)] ?? 99;
                                                                        return leftOrder - rightOrder;
                                                                    })
                                                                    .map((doc, idx) => (
                                                                    <li
                                                                        key={idx}
                                                                        style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                                                    >
                                                                        <i className="fa fa-file-o" style={{ color: '#007bff' }}></i>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link btn-sm"
                                                                            style={{ fontSize: '0.9em', padding: 0 }}
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                const url = doc.document_file_url || doc.document_file;
                                                                                if (url) {
                                                                                    openFilePreview(url, doc.document_type || 'Document');
                                                                                }
                                                                            }}
                                                                        >
                                                                            {doc.document_type || 'Document'}
                                                                            {doc.name ? ` — ${doc.name}` : ''}
                                                                        </button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <span className="text-muted" style={{ fontSize: '0.9em' }}>
                                                                No documents attached
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Payment Processing */}
                {(mode === 'payment' || (mode === 'approval' && stage2DetailsMissing)) && (
                <div className="form-section" style={{ border: '1px solid #007bff', background: '#f0f7ff', padding: '15px', borderRadius: '4px' }}>
                    <h4 style={{ color: '#007bff', marginTop: 0 }}>{mode === 'approval' && stage2DetailsMissing ? 'PAYMENT DETAILS (REQUIRED BEFORE APPROVAL)' : 'PAYMENT PROCESSING'}</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', alignItems: 'end' }}>
                        <div>
                            <label className="required">PAYMENT METHOD (BANK/CASH)</label>
                            <Select2Dropdown
                                options={[{ id: 'CASH', text: 'CASH' }, ...bankOptions]}
                                value={stage2Data.bank}
                                onChange={(val) => setStage2Data(prev => ({
                                    ...prev,
                                    bank: val,
                                    cheque_no: val === 'CASH' ? '' : payables.map(p => p.reference_no).filter(Boolean).join(', ')
                                }))}
                                valueKey="id"
                                displayKey="text"
                                placeholder="SELECT METHOD"
                            />
                        </div>

                        {stage2Data.bank && stage2Data.bank !== 'CASH' && (
                            <div>
                                <label className="required">CHEQUE / REF NO.</label>
                                <input
                                    className="form-control"
                                    value={stage2Data.cheque_no}
                                    onChange={e => setStage2Data(prev => ({ ...prev, cheque_no: e.target.value }))}
                                    placeholder="ENTER CHEQUE NO"
                                    maxLength={200}
                                />
                            </div>
                        )}

                        <div>
                            <label className="required">COLLECTED BY / PAID TO</label>
                            <input
                                className="form-control"
                                value={stage2Data.collected_by}
                                onChange={e => setStage2Data(prev => ({ ...prev, collected_by: e.target.value.toUpperCase() }))}
                                placeholder="ENTER NAME"
                            />
                        </div>

                        <div>
                            <label className="required">PAID BY</label>
                            <Select2Dropdown
                                options={[
                                    { id: 'K & K', text: 'K AND K' },
                                    { id: 'PAID BOC', text: 'PAID ON BEHALF OF CLIENT' }
                                ]}
                                value={stage2Data.paid_by}
                                onChange={(val) => setStage2Data(prev => ({ ...prev, paid_by: val }))}
                                valueKey="id"
                                displayKey="text"
                                placeholder="SELECT PAYER"
                            />
                        </div>
                    </div>
                </div>
                )}

                {/* Reject Input Section */}
                {showRejectInput && (
                    <div style={{ marginTop: '15px', padding: '15px', background: '#fff3f3', border: '1px solid #ffcccc', borderRadius: '4px' }}>
                        <label style={{ fontWeight: 'bold', color: '#c0392b' }}>
                            REJECTION — applies to all {payables.length} payable(s)
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: '15px', marginTop: '8px' }}>
                            <div>
                                <small style={{ fontWeight: 'bold' }}>REASON:</small>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={rejectReason}
                                    onChange={e => setRejectReason(e.target.value.toUpperCase())}
                                    placeholder="Enter reason for rejection..."
                                />
                            </div>
                            <div>
                                <small style={{ fontWeight: 'bold' }}>SEND TO:</small>
                                <Select2Dropdown
                                    options={getRejectionOptions()}
                                    value={rejectionTarget}
                                    onChange={setRejectionTarget}
                                    valueKey="id"
                                    displayKey="text"
                                    placeholder="SELECT TARGET STAGE"
                                    popoutMode={popoutMode}
                                />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button
                                className="btn btn-danger"
                                onClick={() => {
                                    if (!rejectReason.trim()) { notifyError('Please enter a rejection reason'); return; }
                                    if (!rejectionTarget) { notifyError('Please select the stage to send to'); return; }
                                    setShowConfirmReject(true);
                                }}
                                disabled={submitting}
                            >
                                Confirm Reject All
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => { setShowRejectInput(false); setRejectReason(''); setRejectionTarget(null); }}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* Progress Bar */}
                {submitting && progress.total > 0 && (
                    <div style={{ marginTop: '15px', padding: '10px', background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: '4px' }}>
                        <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>
                            Processing {progress.done} of {progress.total}...
                        </div>
                        <div style={{ height: '10px', background: '#e0e0e0', borderRadius: '5px', overflow: 'hidden' }}>
                            <div style={{
                                height: '100%',
                                width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%`,
                                background: '#4caf50',
                                borderRadius: '5px',
                                transition: 'width 0.3s ease'
                            }}></div>
                        </div>
                    </div>
                )}

                {/* Confirm Approve Dialog */}
                {showConfirmApprove && (
                    <div className="modal-overlay">
                        <div className="dialog-container" style={{ width: '520px', maxWidth: '95%' }}>
                            <div className="dialog-header" style={{ background: '#0d6efd', color: '#fff', borderRadius: '4px 4px 0 0' }}>
                                <h3 style={{ margin: 0, color: '#fff' }}>{mode === 'approval' ? 'Approve All Payables' : 'Approve All Payables'}</h3>
                            </div>
                            <div className="dialog-body" style={{ padding: '20px' }}>
                                <p style={{ marginBottom: '10px', fontSize: '1rem' }}>
                                    Are you sure you want to {mode === 'approval' ? 'approve' : 'approve'} all <strong>{payables.length}</strong> payable(s) for <strong>{vendorName}</strong>?
                                </p>
                                {mode === 'payment' && (
                                <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '4px', fontSize: '0.9em' }}>
                                    <div><strong>Method:</strong> {bankOptions.find(b => b.id === stage2Data.bank)?.text || stage2Data.bank}</div>
                                    {stage2Data.cheque_no && <div><strong>Ref/Cheque:</strong> {stage2Data.cheque_no}</div>}
                                    <div><strong>Collected By:</strong> {stage2Data.collected_by}</div>
                                    <div><strong>Paid By:</strong> {[{ id: 'K & K', text: 'K AND K' }, { id: 'PAID BOC', text: 'PAID ON BEHALF OF CLIENT' }].find(o => o.id === stage2Data.paid_by)?.text || stage2Data.paid_by}</div>
                                    <div><strong>Amount:</strong> {Object.entries(totals).map(([currency, data]) => `${currency} ${formatCurrency(data.gross)}`).join(', ')}</div>
                                </div>
                                )}
                            </div>
                            <div className="dialog-footer">
                                <button className="dialog-btn dialog-btn-primary" onClick={approveAll} disabled={submitting}>
                                    Yes, Approve All
                                </button>
                                <button className="dialog-btn dialog-btn-secondary" onClick={() => setShowConfirmApprove(false)} disabled={submitting}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Confirm Reject Dialog */}
                {showConfirmReject && (
                    <div className="modal-overlay">
                        <div className="dialog-container" style={{ width: '520px', maxWidth: '95%' }}>
                            <div className="dialog-header" style={{ background: '#dc3545', color: '#fff', borderRadius: '4px 4px 0 0' }}>
                                <h3 style={{ margin: 0, color: '#fff' }}>Reject All Payables</h3>
                            </div>
                            <div className="dialog-body" style={{ padding: '20px' }}>
                                <p style={{ marginBottom: '10px', fontSize: '1rem' }}>
                                    Are you sure you want to reject all <strong>{payables.length}</strong> payable(s)?
                                </p>
                                <div style={{ padding: '10px', background: '#fff3f3', borderRadius: '4px', fontSize: '0.9em', color: '#c0392b' }}>
                                <div><strong>Reason:</strong> {rejectReason.toUpperCase()}</div>
                                <div style={{ marginTop: '6px' }}><strong>Send To:</strong> {getRejectionOptions().find(o => o.id === rejectionTarget)?.text || rejectionTarget}</div>
                            </div>
                            </div>
                            <div className="dialog-footer">
                                <button
                                    className="dialog-btn"
                                    style={{ background: '#dc3545', color: '#fff', border: '1px solid #dc3545' }}
                                    onClick={rejectAll}
                                    disabled={submitting}
                                >
                                    Yes, Reject All
                                </button>
                                <button className="dialog-btn dialog-btn-secondary" onClick={() => setShowConfirmReject(false)} disabled={submitting}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="dialog-footer" style={{ justifyContent: 'space-between' }}>
                {mode === 'approval' ? (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'nowrap' }}>
                        <div style={{ minWidth: '180px' }}>
                            <small style={{ fontWeight: 'bold', fontSize: '9px' }}>PAYMENT DATE</small>
                            <input
                                type="date"
                                className="form-control"
                                value={stage3Data.payment_date}
                                onChange={e => setStage3Data({ ...stage3Data, payment_date: e.target.value })}
                            />
                        </div>
                        <div style={{ minWidth: '220px' }}>
                            <small style={{ fontWeight: 'bold', fontSize: '9px' }}>TISS / TIPS</small>
                            <Select2Dropdown
                                options={[{ id: 'NO TISS/TIPS', text: 'NO TISS/TIPS' }, { id: 'TISS', text: 'TISS' }, { id: 'TIPS', text: 'TIPS' }]}
                                value={stage3Data.tiss_tips}
                                onChange={val => setStage3Data({ ...stage3Data, tiss_tips: val })}
                                valueKey="id"
                                displayKey="text"
                                placeholder="PLEASE SELECT AN OPTION"
                                menuPlacement="top"
                                popoutMode={popoutMode}
                            />
                        </div>
                        {isTissOrTipsSelected && (
                            <div style={{ minWidth: '170px' }}>
                                <small style={{ fontWeight: 'bold', fontSize: '9px' }}>TISS / TIPS AMOUNT</small>
                                <div style={{ fontWeight: 'bold', lineHeight: '38px', fontSize: '12px' }}>
                                    {`${formatCurrency(stage3TissTipsAmount)} TZS`}
                                </div>
                            </div>
                        )}
                        {isTissOrTipsSelected && (
                            <div style={{ minWidth: '180px' }}>
                                <small style={{ fontWeight: 'bold', fontSize: '9px' }}>NEW PAYABLE</small>
                                <div style={{ fontWeight: 'bold', color: '#0b5ed7', lineHeight: '38px', fontSize: '12px' }}>
                                    {`${formatCurrency(stage3NewPayableAmount)} TZS`}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <button className="dialog-btn dialog-btn-secondary" onClick={onClose} disabled={submitting}>
                        Cancel
                    </button>
                )}
                <div style={{ display: 'flex', gap: '10px' }}>
                    {!showRejectInput && (
                        <button
                            className="btn btn-danger"
                            onClick={() => setShowRejectInput(true)}
                            disabled={submitting}
                            style={{ borderColor: '#dc3545', color: 'white' }}
                        >
                            <i className="fa fa-times"></i> Reject All
                        </button>
                    )}
                    <button
                        className="btn btn-success"
                        onClick={() => { if (validatePayment()) setShowConfirmApprove(true); }}
                        disabled={submitting}
                        style={{ backgroundColor: 'green', borderColor: 'green', color: 'white' }}
                    >
                        <i className="fa fa-check"></i> Approve All ({payables.length})
                    </button>
                    {mode === 'approval' && (
                        <button className="dialog-btn dialog-btn-secondary" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            {viewingPayable && (
                <PopoutWindow
                    title={`View Payable - ${viewingPayable.reference_no || viewingPayable.id}`}
                    name={`payable_view_${(String(viewingPayable.reference_no || viewingPayable.id).replace(/[^a-zA-Z0-9]/g, '_'))}`}
                    onClose={() => setViewingPayable(null)}
                    toastContainerId="payable-popout-toast"
                >
                    {viewingPayable._payable_source === 'shipping' ? (
                        <ShippingLinePayablesForm
                            mode="view"
                            initialData={viewingPayable}
                            onClose={() => setViewingPayable(null)}
                            onSave={() => setViewingPayable(null)}
                            popoutMode={true}
                            toastContainerId="payable-popout-toast"
                        />
                    ) : (
                        <OtherPayablesForm
                            mode="view"
                            initialData={viewingPayable}
                            onClose={() => setViewingPayable(null)}
                            onSave={() => setViewingPayable(null)}
                            popoutMode={true}
                            toastContainerId="payable-popout-toast"
                        />
                    )}
                </PopoutWindow>
            )}
        </div>
    );
};

export default BulkPayForm;
